import { ReactNode, useRef } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useMember } from "../../context/memberContext";
import { useTranslations } from "../../hooks/useTranslations";
import { SP, useChart4Data } from "../../http/serviceportalApi";
import { getMonthlyChartData } from "../../selectors/accountingCharts";
import {
  colorCycle,
  formatAsNumberWith,
  getLongMonthName,
  range,
} from "../../utils";
import SVGBullet from "../icons/Bullet";
import Chart from "../ui/Chart";
import ChartTooltip from "../ui/ChartTooltip";
import ErrorText from "../ui/ErrorText";
import LoadingSpinner from "../ui/LoadingSpinner";
import styles from "./Chart4.module.css";

interface Props {
  seacNumber: string;
  filters: ReactNode;
  companyId?: SP.CompanyId;
  period: SP.Period;
  className?: string;
}

const Chart4 = ({
  seacNumber,
  filters,
  companyId,
  period,
  className,
}: Props) => {
  const { i18n } = useTranslations();
  const { member } = useMember();
  if (!member) return null;

  const aborter = useRef(new AbortController());
  const { data, isValidating, error } = useChart4Data(
    member.id,
    seacNumber,
    period,
    !companyId && member.companies?.length === 1
      ? member.companies[0]?.id
      : companyId,
    aborter.current.signal,
  );
  const isSuccess = !isValidating && !error;
  const chart4Data = data ?? [];

  const yearsRange = range(0, 2);

  return (
    <Chart
      className={className}
      title={i18n.bilanzOnline.accountingCharts.chart4.title}
    >
      <Chart.Filter>{filters}</Chart.Filter>
      <Chart.Content>
        {isSuccess &&
          (chart4Data.length === 0 ? (
            <div className={styles.status}>
              {i18n.bilanzOnline.accountingCharts.noData}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={getMonthlyChartData(
                  chart4Data,
                  period.until.year,
                  (rows, year) =>
                    rows
                      .filter((row) => row.year === year)
                      .reduce((state, value) => state + value.value, 0),
                )}
              >
                <CartesianGrid
                  vertical={false}
                  stroke="var(--ci-light-grey)"
                  strokeDasharray="3 3"
                />
                <XAxis
                  dataKey="month"
                  tickFormatter={(value) => getLongMonthName(value)}
                  axisLine={{ stroke: "var(--ci-light-grey)" }}
                  tickLine={{ stroke: "var(--ci-light-grey)" }}
                  stroke="var(--ci-grey)"
                  strokeWidth="2px"
                />
                <YAxis
                  tickFormatter={(value) => formatAsNumberWith(0, value)}
                  axisLine={{ stroke: "var(--ci-light-grey)" }}
                  tickLine={{ stroke: "var(--ci-light-grey)" }}
                  stroke="var(--ci-grey)"
                  strokeWidth="2px"
                  width={111}
                />
                <Tooltip
                  content={
                    <ChartTooltip
                      labelFormatter={(label) => getLongMonthName(label)}
                      formatter={(value: number) =>
                        formatAsNumberWith(0, value)
                      }
                    />
                  }
                  cursor={{ fill: "var(--white-hover)" }}
                />
                <Legend
                  iconSize={24}
                  wrapperStyle={{ paddingTop: "1em" }}
                  payload={yearsRange.map((x) => ({
                    key: x,
                    value: period.until.year - x,
                    color: "var(--ci-grey)",
                    legendIcon: <SVGBullet fill={colorCycle(x)} />,
                  }))}
                />
                {yearsRange.map((x) => (
                  <Bar
                    key={x}
                    name={"" + (period.until.year - x)}
                    dataKey={`year${x + 1}`}
                    fill={colorCycle(x)}
                  ></Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          ))}
        {isValidating && (
          <div className={styles.status}>
            <LoadingSpinner />
          </div>
        )}
        {error && (
          <div className={styles.status}>
            <ErrorText text={i18n.bilanzOnline.accountingCharts.chart4.error} />
          </div>
        )}
      </Chart.Content>
    </Chart>
  );
};

export default Chart4;
